import React, { useMemo } from 'react';
import { 
  Upload, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  X, 
  Loader2,
  Cpu
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { clearFirmwareUpdateProgress } from '../redux/device/actions';

interface FirmwareProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FirmwareProgressModal: React.FC<FirmwareProgressModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const dispatch = useDispatch();
  const firmwareUpdateProgress = useSelector((state: any) => state.deviceData?.firmwareUpdateProgress || {});
  const devices = useSelector((state: any) => state.deviceData?.devices || []);

  const formatTime = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour12: true, 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'in_progress':
      case 'initiated':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'initiated':
        return 'Initiated';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'timeout':
        return 'Timeout';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
      case 'timeout':
        return 'text-red-600';
      case 'in_progress':
      case 'initiated':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const getDeviceData = (deviceId: string) => {
    return devices.find((d: any) => d.id === deviceId) || null;
  };

  const activeUpdates = Object.values(firmwareUpdateProgress).filter((progress: any) => 
    progress.status === 'initiated' || 
    progress.status === 'in_progress' || 
    progress.status === 'completed' || 
    progress.status === 'failed'
  );

  const completedCount = activeUpdates.filter((p: any) => p.status === 'completed').length;
  const failedCount = activeUpdates.filter((p: any) => p.status === 'failed').length;
  const inProgressCount = activeUpdates.filter((p: any) => 
    p.status === 'initiated' || p.status === 'in_progress'
  ).length;

  // Sort updates by startTime in descending order (newest first)
  const sortedUpdates = useMemo(() => {
    return [...activeUpdates].sort((a: any, b: any) => {
      const timeA = a.startTime ? new Date(a.startTime).getTime() : 0;
      const timeB = b.startTime ? new Date(b.startTime).getTime() : 0;
      return timeB - timeA;
    });
  }, [activeUpdates]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden z-[9997]" style={{ zIndex: 9997 }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Firmware Update Progress
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{inProgressCount}</div>
                <div className="text-sm text-gray-600">In Progress</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{completedCount}</div>
                <div className="text-sm text-gray-600">Completed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{failedCount}</div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
            </div>
          </div>

          {/* Update List */}
          {sortedUpdates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Cpu className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No active firmware updates</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedUpdates.map((update: any) => {
                const device = getDeviceData(update.deviceId);
                const deviceData = device?.deviceData || {};
                
                return (
                  <div
                    key={update.deviceId}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors relative"
                  >
                    {/* Dismiss button - only show for completed/failed updates (not in progress) */}
                    {(update.status === 'completed' || update.status === 'failed') && (
                      <button
                        onClick={() => {
                          dispatch(clearFirmwareUpdateProgress(update.deviceId));
                        }}
                        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
                        title="Dismiss this update"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                    
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0 pr-6">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusIcon(update.status)}
                          <span className={`font-medium ${getStatusColor(update.status)}`}>
                            {getStatusText(update.status)}
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatTime(update.startTime)}
                          </span>
                        </div>
                        
                        {/* Device Details */}
                        <div className="space-y-2 mb-3 bg-gray-50 p-3 rounded-lg">
                          <div className="text-sm font-medium text-gray-900 mb-2">
                            Device Details:
                          </div>
                          
                          {device && (
                            <div className="text-xs text-gray-700 space-y-1">
                              <div><span className="font-semibold">Device ID:</span> {update.deviceId}</div>
                              {deviceData.uid && (
                                <div><span className="font-semibold">UID:</span> {deviceData.uid}</div>
                              )}
                              {deviceData.ip_address && (
                                <div><span className="font-semibold">IP Address:</span> {deviceData.ip_address}</div>
                              )}
                              {deviceData.process_type && (
                                <div><span className="font-semibold">Process Type:</span> {deviceData.process_type}</div>
                              )}
                              {deviceData.platform && (
                                <div><span className="font-semibold">Platform:</span> {deviceData.platform}</div>
                              )}
                              {deviceData.weld_type && (
                                <div><span className="font-semibold">Weld Type:</span> {deviceData.weld_type}</div>
                              )}
                            </div>
                          )}
                          {!device && (
                            <div className="text-xs text-gray-600">
                              Device ID: {update.deviceId}
                            </div>
                          )}
                        </div>

                        {/* Progress Bar */}
                        {(update.status === 'in_progress' || update.status === 'initiated') && (
                          <div className="space-y-2">
                            <div className="flex justify-between text-xs text-gray-600">
                              <span>Firmware update in progress...</span>
                            </div>
                            <Progress value={update.status === 'initiated' ? 10 : 50} className="h-2" />
                          </div>
                        )}

                        {/* Error Message */}
                        {update.status === 'failed' && update.error && (
                          <div className="text-sm text-readable-red bg-[rgba(196,0,31,0.06)] p-2 rounded mt-2">
                            Error: {update.error}
                          </div>
                        )}

                        {/* Success Message */}
                        {update.status === 'completed' && (
                          <div className="text-sm text-green-600 bg-green-50 p-2 rounded mt-2">
                            <div className="font-semibold mb-1">Firmware update successful</div>
                            {update.firmwareVersion && (
                              <div className="text-xs">Firmware Version: {update.firmwareVersion}</div>
                            )}
                          </div>
                        )}

                        {/* Failed Message with version info */}
                        {update.status === 'failed' && update.firmwareVersion && (
                          <div className="text-xs text-gray-500 mt-2">
                            Current Firmware Version: {update.firmwareVersion}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-between items-center">
          {/* Dismiss All button - only show if there are completed/failed updates */}
          {activeUpdates.some((u: any) => u.status === 'completed' || u.status === 'failed') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                activeUpdates.forEach((update: any) => {
                  if (update.status === 'completed' || update.status === 'failed') {
                    dispatch(clearFirmwareUpdateProgress(update.deviceId));
                  }
                });
              }}
            >
              Dismiss All
            </Button>
          )}
          <div className="ml-auto">
            <Button
              size="sm"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

